import { SpaceType } from 'src/components/SearchBar/types'
import { ICoordinates, SizeFilter } from 'src/types/graphql'
import { IQueryParams } from 'src/types/types'

export enum Competitors {
  UStorage = 'u-storage',
  Multibodegas = 'multibodegas',
  MasEspacio = 'mas-espacio',
  GuardaBox = 'guardabox',
  Spakio = 'spakio'
}

export enum PillarPage {
  SelfStorage = 'renta-mini-bodegas',
  Parking = 'pension-estacionamiento',
  Warehouse = 'renta-bodegas-comerciales',
  Industrial = 'renta-naves-industriales'
}

type PillarCrossLink = {
  href: string
  title: string
  subtitle: string
}

type PillarPageConfig = {
  searchName: string
  /** Alternative H1 text for city/hood pages (falls back to searchName) */
  searchNameH1?: string
  label: string
  query: Partial<IQueryParams>
  keywords: string[]
  seoTitle?: string
  seoDescription?: string
  startingPrice: string
  priceRange: { low: number; high: number }
  showSizeSection?: boolean
  showPromoBanner?: boolean
  crossLink?: PillarCrossLink
}

export const PILLAR_PAGE_MAP: { [key in PillarPage]: PillarPageConfig } = {
  [PillarPage.SelfStorage]: {
    searchName: 'Mini bodegas en renta',
    label: 'Mini Bodegas',
    seoTitle: 'Renta de Mini Bodegas en México | Desde $599/mes | SpotMe',
    seoDescription:
      '✓ Mini bodegas desde $599/mes ✓ Sin contratos largos ✓ 15+ ciudades. Compara precios y reserva en línea.',
    startingPrice: '$599',
    priceRange: { low: 599, high: 15000 },
    showSizeSection: true,
    showPromoBanner: true,
    query: {
      canStoreVehicle: false,
      sizes: [
        SizeFilter.Xsmall,
        SizeFilter.Small,
        SizeFilter.Medium,
        SizeFilter.Large
      ],
      spaceTypes: [SpaceType.SELF_STORAGE]
    },
    keywords: [
      'mini bodegas',
      'mini bodegas en renta',
      'minibodegas',
      'self storage',
      'renta de mini bodegas',
      'renta mini bodegas',
      'bodegas en renta',
      'renta de bodegas pequeñas',
      'bodegas pequeñas en renta',
      'minibodegas cdmx',
      'mini bodegas en renta cdmx',
      'renta de minibodegas',
      'mini bodegas cerca de mi'
    ]
  },
  [PillarPage.Parking]: {
    searchName: 'Pensiones de estacionamiento',
    searchNameH1: 'Estacionamientos en renta',
    label: 'Estacionamientos',
    seoTitle: 'Estacionamiento por Mes Cerca de Ti | Pensiones | SpotMe',
    seoDescription:
      'Compara estacionamientos y pensiones verificadas cerca de ti en 15+ ciudades de México. Desde $800/mes, sin contrato. CDMX, Monterrey, Guadalajara y más.',
    // National floor, re-measured 2026-10-14 with `yarn price-floors` (server):
    // $800 in Guadalajara. The old $500 rested on a Veracruz lot that is gone.
    startingPrice: '$800',
    priceRange: { low: 800, high: 5000 },
    showSizeSection: true,
    showPromoBanner: true,
    crossLink: {
      href: '/pension-estacionamiento/flotillas',
      title: '¿Necesitas pensión para tu flotilla?',
      subtitle:
        'Terrenos y espacios para 10, 20 o 50+ vehículos con precios por volumen.'
    },
    query: {
      canStoreVehicle: true,
      sizes: [SizeFilter.Parking],
      spaceTypes: [SpaceType.PARKING]
    },
    keywords: [
      'estacionamiento cdmx',
      'estacionamiento cerca de mi',
      'estacionamientos cerca de mi',
      'pension estacionamiento',
      'pension de autos',
      'renta de estacionamiento',
      'estacionamiento en renta',
      'estacionamientos en renta',
      'pensiones para autos cerca de mi',
      'pension autos cerca de mi',
      'pension autos 24 horas',
      'pension de carros',
      'renta de cajon de estacionamiento',
      'estacionamiento aeropuerto cdmx',
      'estacionamiento guadalajara',
      'estacionamiento puebla',
      'pensiones de autos cerca de mi'
    ]
  },
  [PillarPage.Warehouse]: {
    searchName: 'Bodegas comerciales en renta',
    label: 'Bodegas Comerciales',
    seoTitle: 'Bodegas en Renta en México | Desde $5,000/mes | SpotMe',
    seoDescription:
      '✓ Bodegas en renta desde $5,000/mes ✓ Espacios comerciales verificados ✓ CDMX, MTY, GDL y más. Compara en línea.',
    startingPrice: '$5,000',
    priceRange: { low: 5000, high: 80000 },
    query: {
      canStoreVehicle: false,
      sizes: [SizeFilter.Commercial, SizeFilter.Industrial],
      spaceTypes: [SpaceType.WAREHOUSE]
    },
    keywords: [
      'bodegas comerciales en renta',
      'bodega en renta',
      'renta de bodegas',
      'bodegas en renta',
      'bodegas en renta cdmx',
      'renta de bodegas cdmx',
      'renta bodega comercial',
      'bodega comercial en renta',
      'bodegas grandes en renta',
      'renta de bodega comercial'
    ]
  },
  [PillarPage.Industrial]: {
    searchName: 'Naves industriales en renta',
    label: 'Naves Industriales',
    seoTitle: 'Renta de Naves Industriales | Nearshoring Verificado | SpotMe',
    seoDescription:
      'Naves industriales en renta para nearshoring, manufactura y logística. Espacios verificados en CDMX, Monterrey, Guadalajara y más en SpotMe.',
    startingPrice: '$25,000',
    priceRange: { low: 25000, high: 500000 },
    // Industrial pillar = `Nave` only (3000+ m²). Excludes `Industrial`
    // bucket (500-3000 m²) — those are bodega comercial per
    // `resolvePillarCategory` and show up on `PillarPage.Warehouse`.
    // Keeps the URL classifier + the search filter in agreement: a
    // listing's canonical pillar URL matches the pillar pages it
    // appears on.
    query: {
      canStoreVehicle: false,
      sizes: [SizeFilter.Nave],
      spaceTypes: [SpaceType.INDUSTRIAL_WAREHOUSE]
    },
    keywords: [
      'renta de naves industriales',
      'naves industriales en renta',
      'nave industrial en renta',
      'naves industriales',
      'naves industriales monterrey',
      'naves industriales en renta cdmx',
      'renta espacios industriales nearshoring',
      'arrendamiento espacios manufactura',
      'parques industriales maquiladoras',
      'bodegas industriales en renta',
      'naves industriales guadalajara',
      'naves industriales queretaro'
    ]
  }
}

export const PILLAR_HOWTO_MAP: Record<
  PillarPage,
  { name: string; steps: { name: string; text: string }[] }
> = {
  [PillarPage.SelfStorage]: {
    name: 'Cómo rentar una minibodega en SpotMe',
    steps: [
      {
        name: 'Busca tu espacio',
        text: 'Explora las minibodegas disponibles cerca de ti filtrando por ubicación, tamaño y precio.'
      },
      {
        name: 'Compara opciones',
        text: 'Compara precios, fotos, reseñas y características de cada espacio verificado.'
      },
      {
        name: 'Reserva en línea',
        text: 'Selecciona tu minibodega y reserva de forma segura con pago en línea. Sin contratos largos.'
      },
      {
        name: 'Usa tu minibodega',
        text: 'Coordina con el anfitrión para acceder a tu minibodega cuando lo necesites.'
      }
    ]
  },
  [PillarPage.Parking]: {
    name: 'Cómo encontrar pensión de estacionamiento en SpotMe',
    steps: [
      {
        name: 'Busca tu pensión',
        text: 'Encuentra pensiones de estacionamiento disponibles cerca de tu zona filtrando por ubicación, tipo de vehículo y horario.'
      },
      {
        name: 'Compara opciones',
        text: 'Revisa precios, fotos y reseñas de cada pensión verificada. Filtra por techado, 24 horas o tipo de vehículo.'
      },
      {
        name: 'Reserva en línea',
        text: 'Selecciona tu cajón y reserva con pago seguro en línea. Sin contrato largo, cancela cuando quieras.'
      },
      {
        name: 'Usa tu espacio fijo',
        text: 'Coordina el acceso con el anfitrión y disfruta de tu pensión de estacionamiento mes a mes.'
      }
    ]
  },
  [PillarPage.Warehouse]: {
    name: 'Cómo rentar una bodega comercial en SpotMe',
    steps: [
      {
        name: 'Define tus necesidades',
        text: 'Busca bodegas comerciales filtrando por tamaño, ubicación y presupuesto.'
      },
      {
        name: 'Compara opciones',
        text: 'Compara bodegas verificadas con fotos, precios y reseñas de otros inquilinos.'
      },
      {
        name: 'Reserva en línea',
        text: 'Reserva tu bodega comercial de forma segura. Contratos flexibles sin permanencia mínima.'
      },
      {
        name: 'Comienza a operar',
        text: 'Coordina el acceso y comienza a usar tu bodega para tu negocio.'
      }
    ]
  },
  [PillarPage.Industrial]: {
    name: 'Cómo rentar una nave industrial en SpotMe',
    steps: [
      {
        name: 'Define tus requisitos',
        text: 'Busca naves industriales por tamaño, ubicación y especificaciones técnicas.'
      },
      {
        name: 'Compara naves',
        text: 'Revisa las opciones verificadas con detalles de cada nave industrial.'
      },
      {
        name: 'Reserva en línea',
        text: 'Contacta al anfitrión y reserva tu nave industrial de forma segura.'
      },
      {
        name: 'Inicia operaciones',
        text: 'Coordina el acceso a tu nave y comienza tus operaciones industriales.'
      }
    ]
  }
}

export type IPersonalizedNs =
  | 'page_bodegas_en_renta'
  | 'page_mini_bodega'
  | 'page_estacionamiento_y_pension'
  | 'page_naves_industriales'

export const PILLAR_NS_MAP: Record<PillarPage, IPersonalizedNs> = {
  [PillarPage.SelfStorage]: 'page_mini_bodega',
  [PillarPage.Parking]: 'page_estacionamiento_y_pension',
  [PillarPage.Warehouse]: 'page_bodegas_en_renta',
  [PillarPage.Industrial]: 'page_naves_industriales'
}

export enum SearchPageCity {
  Base = '',

  /** Nuevo León */
  Monterrey = 'monterrey',
  Apodaca = 'apodaca',
  Cumbres = 'cumbres',
  Escobedo = 'general-escobedo',
  Guadalupe = 'guadalupe',
  SantaCatarina = 'santa-catarina',
  SanNicolas = 'san-nicolas',
  SanPedro = 'san-pedro-garza-garcia',
  Linares = 'linares', // new
  Montemorelos = 'montemorelos', // new

  /** Ciudad de México */
  CiudadDeMexico = 'ciudad-de-mexico',

  /** Jalisco */
  Guadalajara = 'guadalajara',
  PuertoVallarta = 'puerto-vallarta',
  Zapopan = 'zapopan',
  Tlaquepaque = 'san-pedro-tlaquepaque',
  Tonala = 'tonala',
  Tlajomulco = 'tlajomulco-de-zuniga',
  ElSalto = 'el-salto',
  LagosDeMoreno = 'lagos-de-moreno', // new
  TepatitlanDeMorelos = 'tepatitlan-de-morelos', // new
  CiudadGuzman = 'ciudad-guzman', // new
  Ocotlan = 'ocotlan', // new
  AutlanDeNavarro = 'autlan-de-navarro', // new
  Arandas = 'arandas', // new

  /** Aguascalientes */
  Aguascalientes = 'aguascalientes',
  JesusMaria = 'jesus-maria',

  /** Baja California */
  Tijuana = 'tijuana',
  Mexicali = 'mexicali',
  PlayasDeRosarito = 'playas-de-rosarito',
  Tecate = 'tecate',
  Ensenada = 'ensenada', // new

  /** Baja California Sur */
  LosCabos = 'los-cabos',
  LaPaz = 'la-paz', // new
  CiudadConstitucion = 'ciudad-constitucion', // new

  /** Campeche */
  Campeche = 'campeche', // new
  CiudadDelCarmen = 'ciudad-del-carmen', // new

  /** Chiapas */
  TuxtlaGutierrez = 'tuxtla-gutierrez', // new
  Tapachula = 'tapachula', // new
  SanCristobalDeLasCasas = 'san-cristobal-de-las-casas', // new
  Comitan = 'comitan', // new

  /** Chihuahua */
  Chihuahua = 'chihuahua',
  CiudadJuarez = 'ciudad-juarez',
  Delicias = 'delicias', // new
  Cuauhtemoc = 'cuauhtemoc', // new
  HidalgoDelParral = 'hidalgo-del-parral', // new
  NuevoCasasGrandes = 'nuevo-casas-grandes', // new

  /** Coahuila */
  Saltillo = 'saltillo',
  Torreon = 'torreon',
  RamosArizpe = 'ramos-arizpe',
  Monclova = 'monclova', // new
  PiedrasNegras = 'piedras-negras', // new
  Acuna = 'acuna', // new

  /** Colima */
  Colima = 'colima', // new
  Manzanillo = 'manzanillo', // new
  Tecoman = 'tecoman', // new

  /** Durango */
  GomezPalacio = 'gomez-palacio',
  Lerdo = 'lerdo',
  Durango = 'durango', // new

  /** Estado de México */
  Toluca = 'toluca',
  Metepec = 'metepec',
  Zinacantepec = 'zinacantepec',
  Atlacomulco = 'atlacomulco', // new

  /** Guanajuato */
  Leon = 'leon',
  Celaya = 'celaya',
  Irapuato = 'irapuato',
  Silao = 'silao',
  Guanajuato = 'guanajuato', // new
  Salamanca = 'salamanca', // new

  /** Guerrero */
  Acapulco = 'acapulco', // new
  Chilpancingo = 'chilpancingo', // new
  Iguala = 'iguala', // new

  /** Hidalgo */
  Pachuca = 'pachuca',
  Tulancingo = 'tulancingo', // new
  TulaDeAllende = 'tula-de-allende', // new
  HuejutlaDeReyes = 'huejutla-de-reyes', // new

  /** Michoacán */
  Morelia = 'morelia',
  Uruapan = 'uruapan', // new
  Zamora = 'zamora', // new
  LazaroCardenas = 'lazaro-cardenas', // new
  Apatzingan = 'apatzingan', // new
  Zitacuaro = 'zitacuaro', // new
  LaPiedad = 'la-piedad', // new
  Sahuayo = 'sahuayo', // new
  CiudadHidalgo = 'ciudad-hidalgo', // new

  /** Morelos */
  Cuernavaca = 'cuernavaca',
  Jiutepec = 'jiutepec',
  Temixco = 'temixco',
  Cuautla = 'cuautla', // new

  /** Nayarit */
  Tepic = 'tepic', // new
  Compostela = 'compostela', // new
  SantiagoIxcuintla = 'santiago-ixcuintla', // new
  // Locality inside the Bahía de Banderas municipality, not a municipality
  // itself — the exception is deliberate. "bucerías" is what renters search and
  // what hosts call the branch; the formal municipality is aliased onto this
  // slug in the backend mirror's ADDRESS_CITY_ALIASES so both spellings resolve
  // to one canonical URL.
  Bucerias = 'bucerias',

  /** Oaxaca */
  Oaxaca = 'oaxaca', // new
  Tuxtepec = 'tuxtepec', // new
  Juchitan = 'juchitan', // new
  SalinaCruz = 'salina-cruz', // new
  HuajuapanDeLeon = 'huajuapan-de-leon', // new

  /** Puebla */
  Puebla = 'puebla',
  SanAndresCholula = 'san-andres-cholula',
  SanPedroCholula = 'san-pedro-cholula',
  Tehuacan = 'tehuacan', // new
  SanMartinTexmelucan = 'san-martin-texmelucan', // new
  Atlixco = 'atlixco', // new
  Teziutlan = 'teziutlan', // new

  /** Querétaro */
  Queretaro = 'queretaro',
  Corregidora = 'corregidora',
  ElMarques = 'el-marques',
  SanJuanDelRio = 'san-juan-del-rio', // new

  /** Quintana Roo */
  Cancun = 'cancun',
  PlayaDelCarmen = 'playa-del-carmen',
  Chetumal = 'chetumal', // new
  Cozumel = 'cozumel', // new

  /** San Luis Potosí */
  SanLuisPotosi = 'san-luis-potosi',
  Soledad = 'soledad-de-graciano-sanchez',
  CiudadValles = 'ciudad-valles', // new
  Matehuala = 'matehuala', // new
  Rioverde = 'rioverde', // new

  /** Sinaloa */
  Mazatlan = 'mazatlan',
  Culiacan = 'culiacan', // new
  LosMochis = 'los-mochis', // new
  Guasave = 'guasave', // new
  Guamuchil = 'guamuchil', // new

  /** Sonora */
  Hermosillo = 'hermosillo',
  CiudadObregon = 'ciudad-obregon', // new
  Nogales = 'nogales', // new
  SanLuisRioColorado = 'san-luis-rio-colorado', // new
  Guaymas = 'guaymas', // new
  Navojoa = 'navojoa', // new
  AguaPrieta = 'agua-prieta', // new
  Caborca = 'caborca', // new

  /** Tabasco */
  Villahermosa = 'villahermosa',
  Cardenas = 'cardenas', // new
  Comalcalco = 'comalcalco', // new

  /** Tamaulipas */
  Tampico = 'tampico',
  Reynosa = 'reynosa',
  CiudadMadero = 'ciudad-madero',
  CiudadVictoria = 'ciudad-victoria', // new
  Matamoros = 'matamoros', // new
  NuevoLaredo = 'nuevo-laredo', // new
  Altamira = 'altamira', // new
  ElMante = 'el-mante', // new

  /** Tlaxcala */
  Tlaxcala = 'tlaxcala', // new
  Apizaco = 'apizaco', // new
  Huamantla = 'huamantla', // new

  /** Veracruz */
  Veracruz = 'veracruz',
  Xalapa = 'xalapa', // new
  Coatzacoalcos = 'coatzacoalcos', // new
  Cordoba = 'cordoba', // new
  Orizaba = 'orizaba', // new
  PozaRica = 'poza-rica', // new
  Minatitlan = 'minatitlan', // new
  Tuxpan = 'tuxpan', // new
  SanAndresTuxtla = 'san-andres-tuxtla', // new
  MartinezDeLaTorre = 'martinez-de-la-torre', // new

  /** Yucatán */
  Merida = 'merida',
  Kanasin = 'kanasin',
  Valladolid = 'valladolid', // new
  Tizimin = 'tizimin', // new

  /** Zacatecas */
  Zacatecas = 'zacatecas', // new
  Fresnillo = 'fresnillo' // new
}

/**
 * Cities pre-rendered at build time. All other cities use fallback: 'blocking'
 * (built on first request, then cached via ISR).
 * Primary + secondary cities + their metro satellites.
 */
export const PRERENDER_CITIES: SearchPageCity[] = [
  // Primary
  SearchPageCity.Monterrey,
  SearchPageCity.CiudadDeMexico,
  SearchPageCity.Guadalajara,
  // Monterrey metro
  SearchPageCity.Apodaca,
  SearchPageCity.Cumbres,
  SearchPageCity.Escobedo,
  SearchPageCity.Guadalupe,
  SearchPageCity.SantaCatarina,
  SearchPageCity.SanNicolas,
  SearchPageCity.SanPedro,
  // Guadalajara metro
  SearchPageCity.Zapopan,
  SearchPageCity.Tlaquepaque,
  SearchPageCity.Tonala,
  SearchPageCity.Tlajomulco,
  SearchPageCity.ElSalto
]

/**
 * Pillars for which we prerender hood pages. B2B pillars (commercial
 * warehouses, industrial) are broker/off-platform and get negligible direct
 * hood-level traffic — let them render on first request via ISR instead.
 */
export const PRERENDER_HOOD_PILLARS: PillarPage[] = [
  PillarPage.SelfStorage,
  PillarPage.Parking
]

export enum MexicoCityHoods {
  Iztapalapa = 'iztapalapa',
  DeValle = 'del-valle',
  Tlalnepantla = 'tlalnepantla',
  ValleDeChalco = 'valle-de-chalco',
  Azcapotzalco = 'azcapotzalco',
  Naucalpan = 'naucalpan',
  Polanco = 'polanco',

  LasLomas = 'las-lomas',
  Centro = 'centro',
  Condesa = 'condesa',
  Roma = 'roma',
  RomaNorte = 'roma-norte',
  RomaSur = 'roma-sur',
  ColoniaJuarez = 'colonia-juarez',
  Coyoacan = 'coyoacan',
  JardinesDelPedregal = 'jardines-del-pedregal',
  LomasDeChapultepec = 'lomas-de-chapultepec',
  Napoles = 'napoles',
  SanAngel = 'san-angel',
  SantaFe = 'santa-fe',
  Tepito = 'tepito',
  Tlatelolco = 'tlatelolco',
  Anzures = 'anzures',
  Narvarte = 'narvarte',
  Escandon = 'escandon',
  SanRafael = 'san-rafael',
  Doctores = 'doctores',
  Guerrero = 'guerrero',
  Obrera = 'obrera',

  Ecatepec = 'ecatepec',
  CuautitlanIzcalli = 'cuautitlan-izcalli',
  Tultitlan = 'tultitlan',
  AtizapanDeZaragoza = 'atizapan-de-zaragoza',
  Texcoco = 'texcoco',
  Chalco = 'chalco',
  Xochimilco = 'xochimilco',
  Tlalpan = 'tlalpan',
  Mixcoac = 'mixcoac',
  Lindavista = 'lindavista'
}

export type SearchHood = MexicoCityHoods

/**
 * Core high-value CDMX hoods prerendered at build time. All other hoods still
 * route via `fallback: 'blocking'` — they render on first hit and cache via
 * ISR, so unlisted hoods are not lost, they just skip the build queue.
 */
export const PRERENDER_HOODS: MexicoCityHoods[] = [
  MexicoCityHoods.Polanco,
  MexicoCityHoods.Condesa,
  MexicoCityHoods.RomaNorte,
  MexicoCityHoods.DeValle,
  MexicoCityHoods.SantaFe,
  MexicoCityHoods.Coyoacan,
  MexicoCityHoods.LomasDeChapultepec,
  MexicoCityHoods.Narvarte,
  MexicoCityHoods.Napoles
]

const MONTERREY_CITIES = [
  SearchPageCity.Monterrey,
  SearchPageCity.Apodaca,
  SearchPageCity.Cumbres,
  SearchPageCity.Escobedo,
  SearchPageCity.Guadalupe,
  SearchPageCity.SantaCatarina,
  SearchPageCity.SanNicolas,
  SearchPageCity.SanPedro
]
const GUADALAJARA_CITIES = [
  SearchPageCity.Guadalajara,
  SearchPageCity.Zapopan,
  SearchPageCity.Tlaquepaque,
  SearchPageCity.Tonala,
  SearchPageCity.Tlajomulco,
  SearchPageCity.ElSalto
]
const TOLUCA_CITIES = [
  SearchPageCity.Toluca,
  SearchPageCity.Metepec,
  SearchPageCity.Zinacantepec
]
const PUEBLA_CITIES = [
  SearchPageCity.Puebla,
  SearchPageCity.SanAndresCholula,
  SearchPageCity.SanPedroCholula
]
const TIJUANA_CITIES = [
  SearchPageCity.Tijuana,
  SearchPageCity.PlayasDeRosarito,
  SearchPageCity.Tecate
]
const QUERETARO_CITIES = [
  SearchPageCity.Queretaro,
  SearchPageCity.Corregidora,
  SearchPageCity.ElMarques
]
const AGUASCALIENTES_CITIES = [
  SearchPageCity.Aguascalientes,
  SearchPageCity.JesusMaria
]
const MERIDA_CITIES = [SearchPageCity.Merida, SearchPageCity.Kanasin]
const SALTILLO_CITIES = [SearchPageCity.Saltillo, SearchPageCity.RamosArizpe]
const SAN_LUIS_POTOSI_CITIES = [
  SearchPageCity.SanLuisPotosi,
  SearchPageCity.Soledad
]
const CUERNAVACA_CITIES = [
  SearchPageCity.Cuernavaca,
  SearchPageCity.Jiutepec,
  SearchPageCity.Temixco
]
const TORREON_CITIES = [
  SearchPageCity.Torreon,
  SearchPageCity.GomezPalacio,
  SearchPageCity.Lerdo
]
const LEON_CITIES = [
  SearchPageCity.Leon,
  SearchPageCity.Irapuato,
  SearchPageCity.Silao,
  SearchPageCity.Celaya
]
const CANCUN_CITIES = [SearchPageCity.Cancun, SearchPageCity.PlayaDelCarmen]
const TAMPICO_CITIES = [
  SearchPageCity.Tampico,
  SearchPageCity.CiudadMadero,
  SearchPageCity.Altamira
]

// Per-state nearby-city groups (geographic related links for standalone cities)
const STATE_NUEVO_LEON = [
  SearchPageCity.Monterrey,
  SearchPageCity.Apodaca,
  SearchPageCity.Cumbres,
  SearchPageCity.Escobedo,
  SearchPageCity.Guadalupe,
  SearchPageCity.SantaCatarina,
  SearchPageCity.SanNicolas,
  SearchPageCity.SanPedro,
  SearchPageCity.Linares,
  SearchPageCity.Montemorelos
]
const STATE_JALISCO = [
  SearchPageCity.Guadalajara,
  SearchPageCity.PuertoVallarta,
  SearchPageCity.Zapopan,
  SearchPageCity.Tlaquepaque,
  SearchPageCity.Tonala,
  SearchPageCity.Tlajomulco,
  SearchPageCity.ElSalto,
  SearchPageCity.LagosDeMoreno,
  SearchPageCity.TepatitlanDeMorelos,
  SearchPageCity.CiudadGuzman,
  SearchPageCity.Ocotlan,
  SearchPageCity.AutlanDeNavarro,
  SearchPageCity.Arandas
]
const STATE_BAJA_CALIFORNIA = [
  SearchPageCity.Tijuana,
  SearchPageCity.Mexicali,
  SearchPageCity.PlayasDeRosarito,
  SearchPageCity.Tecate,
  SearchPageCity.Ensenada
]
const STATE_BAJA_CALIFORNIA_SUR = [
  SearchPageCity.LosCabos,
  SearchPageCity.LaPaz,
  SearchPageCity.CiudadConstitucion
]
const STATE_CAMPECHE = [SearchPageCity.Campeche, SearchPageCity.CiudadDelCarmen]
const STATE_CHIAPAS = [
  SearchPageCity.TuxtlaGutierrez,
  SearchPageCity.Tapachula,
  SearchPageCity.SanCristobalDeLasCasas,
  SearchPageCity.Comitan
]
const STATE_CHIHUAHUA = [
  SearchPageCity.Chihuahua,
  SearchPageCity.CiudadJuarez,
  SearchPageCity.Delicias,
  SearchPageCity.Cuauhtemoc,
  SearchPageCity.HidalgoDelParral,
  SearchPageCity.NuevoCasasGrandes
]
const STATE_COAHUILA = [
  SearchPageCity.Saltillo,
  SearchPageCity.Torreon,
  SearchPageCity.RamosArizpe,
  SearchPageCity.Monclova,
  SearchPageCity.PiedrasNegras,
  SearchPageCity.Acuna
]
const STATE_COLIMA = [
  SearchPageCity.Colima,
  SearchPageCity.Manzanillo,
  SearchPageCity.Tecoman
]
const STATE_DURANGO = [
  SearchPageCity.GomezPalacio,
  SearchPageCity.Lerdo,
  SearchPageCity.Durango
]
const STATE_ESTADO_DE_MEXICO = [
  SearchPageCity.Toluca,
  SearchPageCity.Metepec,
  SearchPageCity.Zinacantepec,
  SearchPageCity.Atlacomulco
]
const STATE_GUANAJUATO = [
  SearchPageCity.Leon,
  SearchPageCity.Celaya,
  SearchPageCity.Irapuato,
  SearchPageCity.Silao,
  SearchPageCity.Guanajuato,
  SearchPageCity.Salamanca
]
const STATE_GUERRERO = [
  SearchPageCity.Acapulco,
  SearchPageCity.Chilpancingo,
  SearchPageCity.Iguala
]
const STATE_HIDALGO = [
  SearchPageCity.Pachuca,
  SearchPageCity.Tulancingo,
  SearchPageCity.TulaDeAllende,
  SearchPageCity.HuejutlaDeReyes
]
const STATE_MICHOACAN = [
  SearchPageCity.Morelia,
  SearchPageCity.Uruapan,
  SearchPageCity.Zamora,
  SearchPageCity.LazaroCardenas,
  SearchPageCity.Apatzingan,
  SearchPageCity.Zitacuaro,
  SearchPageCity.LaPiedad,
  SearchPageCity.Sahuayo,
  SearchPageCity.CiudadHidalgo
]
const STATE_MORELOS = [
  SearchPageCity.Cuernavaca,
  SearchPageCity.Jiutepec,
  SearchPageCity.Temixco,
  SearchPageCity.Cuautla
]
const STATE_NAYARIT = [
  SearchPageCity.Tepic,
  SearchPageCity.Compostela,
  SearchPageCity.SantiagoIxcuintla,
  SearchPageCity.Bucerias
]
const STATE_OAXACA = [
  SearchPageCity.Oaxaca,
  SearchPageCity.Tuxtepec,
  SearchPageCity.Juchitan,
  SearchPageCity.SalinaCruz,
  SearchPageCity.HuajuapanDeLeon
]
const STATE_PUEBLA = [
  SearchPageCity.Puebla,
  SearchPageCity.SanAndresCholula,
  SearchPageCity.SanPedroCholula,
  SearchPageCity.Tehuacan,
  SearchPageCity.SanMartinTexmelucan,
  SearchPageCity.Atlixco,
  SearchPageCity.Teziutlan
]
const STATE_QUERETARO = [
  SearchPageCity.Queretaro,
  SearchPageCity.Corregidora,
  SearchPageCity.ElMarques,
  SearchPageCity.SanJuanDelRio
]
const STATE_QUINTANA_ROO = [
  SearchPageCity.Cancun,
  SearchPageCity.PlayaDelCarmen,
  SearchPageCity.Chetumal,
  SearchPageCity.Cozumel
]
const STATE_SAN_LUIS_POTOSI = [
  SearchPageCity.SanLuisPotosi,
  SearchPageCity.Soledad,
  SearchPageCity.CiudadValles,
  SearchPageCity.Matehuala,
  SearchPageCity.Rioverde
]
const STATE_SINALOA = [
  SearchPageCity.Mazatlan,
  SearchPageCity.Culiacan,
  SearchPageCity.LosMochis,
  SearchPageCity.Guasave,
  SearchPageCity.Guamuchil
]
const STATE_SONORA = [
  SearchPageCity.Hermosillo,
  SearchPageCity.CiudadObregon,
  SearchPageCity.Nogales,
  SearchPageCity.SanLuisRioColorado,
  SearchPageCity.Guaymas,
  SearchPageCity.Navojoa,
  SearchPageCity.AguaPrieta,
  SearchPageCity.Caborca
]
const STATE_TABASCO = [
  SearchPageCity.Villahermosa,
  SearchPageCity.Cardenas,
  SearchPageCity.Comalcalco
]
const STATE_TAMAULIPAS = [
  SearchPageCity.Tampico,
  SearchPageCity.Reynosa,
  SearchPageCity.CiudadMadero,
  SearchPageCity.CiudadVictoria,
  SearchPageCity.Matamoros,
  SearchPageCity.NuevoLaredo,
  SearchPageCity.Altamira,
  SearchPageCity.ElMante
]
const STATE_TLAXCALA = [
  SearchPageCity.Tlaxcala,
  SearchPageCity.Apizaco,
  SearchPageCity.Huamantla
]
const STATE_VERACRUZ = [
  SearchPageCity.Veracruz,
  SearchPageCity.Xalapa,
  SearchPageCity.Coatzacoalcos,
  SearchPageCity.Cordoba,
  SearchPageCity.Orizaba,
  SearchPageCity.PozaRica,
  SearchPageCity.Minatitlan,
  SearchPageCity.Tuxpan,
  SearchPageCity.SanAndresTuxtla,
  SearchPageCity.MartinezDeLaTorre
]
const STATE_YUCATAN = [
  SearchPageCity.Merida,
  SearchPageCity.Kanasin,
  SearchPageCity.Valladolid,
  SearchPageCity.Tizimin
]
const STATE_ZACATECAS = [SearchPageCity.Zacatecas, SearchPageCity.Fresnillo]
function buildMetroParentMap(
  metros: [SearchPageCity, SearchPageCity[]][]
): Partial<Record<SearchPageCity, SearchPageCity>> {
  const map: Partial<Record<SearchPageCity, SearchPageCity>> = {}
  for (const [parent, cities] of metros) {
    for (const city of cities) {
      if (city !== parent) map[city] = parent
    }
  }
  return map
}

export const METRO_PARENT_MAP = buildMetroParentMap([
  [SearchPageCity.Monterrey, MONTERREY_CITIES],
  [SearchPageCity.Guadalajara, GUADALAJARA_CITIES],
  [SearchPageCity.Toluca, TOLUCA_CITIES],
  [SearchPageCity.Puebla, PUEBLA_CITIES],
  [SearchPageCity.Tijuana, TIJUANA_CITIES],
  [SearchPageCity.Queretaro, QUERETARO_CITIES],
  [SearchPageCity.Aguascalientes, AGUASCALIENTES_CITIES],
  [SearchPageCity.Merida, MERIDA_CITIES],
  [SearchPageCity.Saltillo, SALTILLO_CITIES],
  [SearchPageCity.SanLuisPotosi, SAN_LUIS_POTOSI_CITIES],
  [SearchPageCity.Cuernavaca, CUERNAVACA_CITIES],
  [SearchPageCity.Torreon, TORREON_CITIES],
  [SearchPageCity.Leon, LEON_CITIES],
  [SearchPageCity.Cancun, CANCUN_CITIES],
  [SearchPageCity.Tampico, TAMPICO_CITIES]
])

/**
 * Curated list of primary cities shown on the landing page (RecommendedSection city pills).
 * Keeps the UI clean; the directory and sitemaps include all cities.
 */
export const CITIES_FOR_LANDING: SearchPageCity[] = [
  SearchPageCity.CiudadDeMexico,
  SearchPageCity.Monterrey,
  SearchPageCity.Guadalajara,
  SearchPageCity.Tijuana,
  SearchPageCity.Puebla,
  SearchPageCity.Queretaro,
  SearchPageCity.Leon,
  SearchPageCity.Cancun,
  SearchPageCity.Merida,
  SearchPageCity.Aguascalientes
]

export const SEARCH_PAGE_CITY_MAP: {
  [key in SearchPageCity]: {
    city: SearchPageCity
    cityName: string
    cityNameShort?: string
    placeId: string
    hoods?: SearchHood[]
    center?: ICoordinates
    nearbyCities?: SearchPageCity[]
  }
} = {
  [SearchPageCity.Base]: {
    city: SearchPageCity.Base,
    cityName: '',
    placeId: ''
  },
  [SearchPageCity.Monterrey]: {
    city: SearchPageCity.Monterrey,
    cityName: 'Monterrey',
    placeId: 'ChIJ9fg3tDGVYoYRlJjIasrT06M',
    center: { lat: 25.68, lng: -100.31 },
    nearbyCities: MONTERREY_CITIES
  },
  [SearchPageCity.Apodaca]: {
    city: SearchPageCity.Apodaca,
    cityName: 'Apodaca',
    placeId: 'ChIJRX6YMS_vYoYR1Lg9kQNoM38',
    nearbyCities: MONTERREY_CITIES
  },
  [SearchPageCity.Cumbres]: {
    city: SearchPageCity.Cumbres,
    cityName: 'Cumbres',
    placeId: 'ChIJqRUOebWQYoYRprQTVWzm9jA',
    nearbyCities: MONTERREY_CITIES
  },
  [SearchPageCity.Escobedo]: {
    city: SearchPageCity.Escobedo,
    cityName: 'General Escobedo',
    placeId: 'ChIJl9ccRqSTYoYRAoX080KMn0w',
    nearbyCities: MONTERREY_CITIES
  },
  [SearchPageCity.Guadalupe]: {
    city: SearchPageCity.Guadalupe,
    cityName: 'Guadalupe',
    placeId: 'ChIJsTbEclYwY4YRrvU1TovCwVc',
    nearbyCities: MONTERREY_CITIES.filter((c) => c !== SearchPageCity.Guadalupe)
  },
  [SearchPageCity.SantaCatarina]: {
    city: SearchPageCity.SantaCatarina,
    cityName: 'Santa Catarina',
    placeId: 'ChIJHzSQDGyYYoYRpWP_MHAZB3E',
    nearbyCities: MONTERREY_CITIES
  },
  [SearchPageCity.SanNicolas]: {
    city: SearchPageCity.SanNicolas,
    cityName: 'San Nicolás',
    placeId: 'ChIJAw16yguTYoYRpyEPQQtm8ho',
    nearbyCities: MONTERREY_CITIES
  },
  [SearchPageCity.SanPedro]: {
    city: SearchPageCity.SanPedro,
    cityName: 'San Pedro Garza García',
    placeId: 'ChIJqXEv8aC9YoYRrv9zsD_mRA4',
    nearbyCities: MONTERREY_CITIES
  },
  [SearchPageCity.CiudadDeMexico]: {
    city: SearchPageCity.CiudadDeMexico,
    cityName: 'Ciudad de México',
    cityNameShort: 'CDMX',
    placeId: 'ChIJB3UJ2yYAzoURQeheJnYQBlQ',
    hoods: Object.values(MexicoCityHoods),
    center: { lat: 19.42847, lng: -99.12766 }
  },
  [SearchPageCity.Guadalajara]: {
    city: SearchPageCity.Guadalajara,
    cityName: 'Guadalajara',
    placeId: 'ChIJm9MvtYyxKIQRUFeGvwKTPdY',
    center: { lat: 20.66682, lng: -103.39182 },
    nearbyCities: GUADALAJARA_CITIES
  },
  [SearchPageCity.Zapopan]: {
    city: SearchPageCity.Zapopan,
    cityName: 'Zapopan',
    placeId: 'ChIJE0xwqWGuKIQRrALQftSl2K8',
    nearbyCities: GUADALAJARA_CITIES
  },
  [SearchPageCity.Tlaquepaque]: {
    city: SearchPageCity.Tlaquepaque,
    cityName: 'San Pedro Tlaquepaque',
    placeId: 'ChIJA0pBpoezKIQREKq-cByLC14',
    nearbyCities: GUADALAJARA_CITIES
  },
  [SearchPageCity.Tonala]: {
    city: SearchPageCity.Tonala,
    cityName: 'Tonala',
    placeId: 'ChIJW7Bu_fi0KIQRqgroLfZ5CZ0',
    nearbyCities: GUADALAJARA_CITIES
  },
  [SearchPageCity.Tlajomulco]: {
    city: SearchPageCity.Tlajomulco,
    cityName: 'Tlajomulco de Zuniga',
    placeId: 'ChIJk0R9BvdTL4QRL95OIvTG3_k',
    nearbyCities: GUADALAJARA_CITIES
  },
  [SearchPageCity.ElSalto]: {
    city: SearchPageCity.ElSalto,
    cityName: 'El Salto',
    placeId: 'ChIJt9NHh1hKL4QRFqzO2AILfbI',
    nearbyCities: GUADALAJARA_CITIES
  },
  [SearchPageCity.Tijuana]: {
    city: SearchPageCity.Tijuana,
    cityName: 'Tijuana',
    placeId: 'ChIJ03tYJgI52YARViTmpK9LchQ',
    center: { lat: 32.5027, lng: -117.00371 },
    nearbyCities: TIJUANA_CITIES
  },
  [SearchPageCity.Puebla]: {
    city: SearchPageCity.Puebla,
    cityName: 'Puebla',
    placeId: 'ChIJO3q8Xr3Az4URla2U5B1Gpkg',
    center: { lat: 19.03793, lng: -98.20346 },
    nearbyCities: PUEBLA_CITIES
  },
  [SearchPageCity.Queretaro]: {
    city: SearchPageCity.Queretaro,
    cityName: 'Querétaro',
    placeId: 'ChIJVZJb3I9b04URL4MbVqqUsJc',
    center: { lat: 20.58806, lng: -100.38806 },
    nearbyCities: QUERETARO_CITIES
  },
  [SearchPageCity.Leon]: {
    city: SearchPageCity.Leon,
    cityName: 'León',
    placeId: 'ChIJIefm1v--K4QRJ0OlYeyVbWA',
    center: { lat: 21.12908, lng: -101.67374 },
    nearbyCities: LEON_CITIES
  },
  [SearchPageCity.Chihuahua]: {
    city: SearchPageCity.Chihuahua,
    cityName: 'Chihuahua',
    placeId: 'ChIJM0BIXZ1E6oYRex3dBqen8bc',
    center: { lat: 28.63528, lng: -106.08889 },
    nearbyCities: STATE_CHIHUAHUA
  },
  [SearchPageCity.Merida]: {
    city: SearchPageCity.Merida,
    cityName: 'Mérida',
    placeId: 'ChIJFw1Fq1xxVo8RCeurFVcV_F0',
    center: { lat: 20.97537, lng: -89.61696 },
    nearbyCities: MERIDA_CITIES
  },
  [SearchPageCity.Cancun]: {
    city: SearchPageCity.Cancun,
    cityName: 'Cancún',
    placeId: 'ChIJ21P2rgUrTI8Ris1fYjy3Ms4',
    center: { lat: 21.17429, lng: -86.84656 },
    nearbyCities: CANCUN_CITIES
  },
  [SearchPageCity.Aguascalientes]: {
    city: SearchPageCity.Aguascalientes,
    cityName: 'Aguascalientes',
    placeId: 'ChIJ-9nkOhTsKYQRIFKcZ3hpAQQ',
    center: { lat: 21.88234, lng: -102.28259 },
    nearbyCities: AGUASCALIENTES_CITIES
  },
  [SearchPageCity.Hermosillo]: {
    city: SearchPageCity.Hermosillo,
    cityName: 'Hermosillo',
    placeId: 'ChIJ5a7femiEzoYR-X-I6ZVTPbM',
    center: { lat: 29.0729, lng: -110.9559 },
    nearbyCities: STATE_SONORA
  },
  [SearchPageCity.PuertoVallarta]: {
    city: SearchPageCity.PuertoVallarta,
    cityName: 'Puerto Vallarta',
    placeId: 'ChIJf8hjUGNFIYQRdFK_hZm_z3k',
    center: { lat: 20.65342, lng: -105.22531 },
    nearbyCities: STATE_JALISCO
  },
  [SearchPageCity.Toluca]: {
    city: SearchPageCity.Toluca,
    cityName: 'Toluca',
    placeId: 'ChIJuetQKomJzYURxAgCVdVKP60',
    center: { lat: 19.28786, lng: -99.65324 },
    nearbyCities: TOLUCA_CITIES
  },
  [SearchPageCity.LosCabos]: {
    city: SearchPageCity.LosCabos,
    cityName: 'Los Cabos',
    placeId: 'ChIJoQjlifNKr4YRaLiiwWLfpEs',
    // Corridor midpoint — Cabo San Lucas and San José del Cabo are ~30 km
    // apart and both must fall inside the 80 km directory-hub radius.
    center: { lat: 22.9754, lng: -109.8288 },
    nearbyCities: STATE_BAJA_CALIFORNIA_SUR
  },
  [SearchPageCity.CiudadJuarez]: {
    city: SearchPageCity.CiudadJuarez,
    cityName: 'Ciudad Juárez',
    placeId: 'ChIJnTILPcte54YRdHmcVABhGQs',
    center: { lat: 31.6903638, lng: -106.4245478 },
    nearbyCities: STATE_CHIHUAHUA
  },
  [SearchPageCity.Cuernavaca]: {
    city: SearchPageCity.Cuernavaca,
    cityName: 'Cuernavaca',
    placeId: 'ChIJra8im0nezYURoZC3ubW8tsk',
    center: { lat: 18.9242095, lng: -99.2215659 },
    nearbyCities: CUERNAVACA_CITIES
  },
  [SearchPageCity.Saltillo]: {
    city: SearchPageCity.Saltillo,
    cityName: 'Saltillo',
    placeId: 'ChIJ7xnfeJANiIYRwDBHeUGSUak',
    center: { lat: 25.4383234, lng: -100.973678 },
    nearbyCities: SALTILLO_CITIES
  },
  [SearchPageCity.SanLuisPotosi]: {
    city: SearchPageCity.SanLuisPotosi,
    cityName: 'San Luis Potosí',
    placeId: 'ChIJefusBQCiKoQRV4Lhrynu0g4',
    center: { lat: 22.1520892, lng: -100.9733024 },
    nearbyCities: SAN_LUIS_POTOSI_CITIES
  },
  [SearchPageCity.Mexicali]: {
    city: SearchPageCity.Mexicali,
    cityName: 'Mexicali',
    placeId: 'ChIJ0913qAxw14ARmvXN5aAzANQ',
    center: { lat: 32.6249284, lng: -115.4435754 },
    nearbyCities: STATE_BAJA_CALIFORNIA
  },
  [SearchPageCity.Torreon]: {
    city: SearchPageCity.Torreon,
    cityName: 'Torreón',
    placeId: 'ChIJ-7NFu6nbj4YRHaCucJl6zIs',
    center: { lat: 25.5428443, lng: -103.4067861 },
    nearbyCities: TORREON_CITIES
  },
  [SearchPageCity.PlayaDelCarmen]: {
    city: SearchPageCity.PlayaDelCarmen,
    cityName: 'Playa del Carmen',
    placeId: 'ChIJYU4t0iNDTo8R3EqrO3gLweg',
    center: { lat: 20.6295586, lng: -87.0738851 },
    nearbyCities: CANCUN_CITIES
  },
  [SearchPageCity.Metepec]: {
    city: SearchPageCity.Metepec,
    cityName: 'Metepec',
    placeId: 'ChIJcyA8aK6LzYURVqrPoU8_mJQ',
    center: { lat: 19.2528864, lng: -99.598551 },
    nearbyCities: TOLUCA_CITIES
  },
  [SearchPageCity.Zinacantepec]: {
    city: SearchPageCity.Zinacantepec,
    cityName: 'Zinacantepec',
    placeId: 'ChIJh6mfAN-HzYURrr5VdeZBmv0',
    center: { lat: 19.2816191, lng: -99.7316282 },
    nearbyCities: TOLUCA_CITIES
  },
  [SearchPageCity.SanAndresCholula]: {
    city: SearchPageCity.SanAndresCholula,
    cityName: 'San Andrés Cholula',
    placeId: 'ChIJqUy-8cXHz4UR7C-ZvK1Zw_I',
    center: { lat: 19.059957, lng: -98.2949146 },
    nearbyCities: PUEBLA_CITIES
  },
  [SearchPageCity.SanPedroCholula]: {
    city: SearchPageCity.SanPedroCholula,
    cityName: 'San Pedro Cholula',
    placeId: 'ChIJEe-77dzIz4URIhpRIFP0ihU',
    center: { lat: 19.0568652, lng: -98.3052374 },
    nearbyCities: PUEBLA_CITIES
  },
  [SearchPageCity.PlayasDeRosarito]: {
    city: SearchPageCity.PlayasDeRosarito,
    cityName: 'Playas de Rosarito',
    placeId: 'ChIJG87Ocj8x2YARR9J4jRzGDno',
    center: { lat: 32.3661011, lng: -117.0617553 },
    nearbyCities: TIJUANA_CITIES
  },
  [SearchPageCity.Tecate]: {
    city: SearchPageCity.Tecate,
    cityName: 'Tecate',
    placeId: 'ChIJ52JfmqgO2YARbgHNB3z3p4Q',
    center: { lat: 32.563609, lng: -116.6256752 },
    nearbyCities: TIJUANA_CITIES
  },
  [SearchPageCity.Corregidora]: {
    city: SearchPageCity.Corregidora,
    cityName: 'Corregidora',
    placeId: 'ChIJk9vfcB1G04URnoVF2VEIfXk',
    center: { lat: 20.5379233, lng: -100.4386457 },
    nearbyCities: QUERETARO_CITIES
  },
  [SearchPageCity.ElMarques]: {
    city: SearchPageCity.ElMarques,
    cityName: 'El Marqués',
    placeId: 'ChIJx9ysQDVe04URk9eaAkpqfqU',
    center: { lat: 20.6113606, lng: -100.3288581 },
    nearbyCities: QUERETARO_CITIES
  },
  [SearchPageCity.JesusMaria]: {
    city: SearchPageCity.JesusMaria,
    cityName: 'Jesús María',
    placeId: 'ChIJJYhrk__lKYQRSpsgZdEtGx0',
    center: { lat: 21.9635744, lng: -102.3441041 },
    nearbyCities: AGUASCALIENTES_CITIES
  },
  [SearchPageCity.Kanasin]: {
    city: SearchPageCity.Kanasin,
    cityName: 'Kanasín',
    placeId: 'ChIJO0hG02BwVo8RrLSiwOGZ2l4',
    center: { lat: 20.9441486, lng: -89.5639119 },
    nearbyCities: MERIDA_CITIES
  },
  [SearchPageCity.RamosArizpe]: {
    city: SearchPageCity.RamosArizpe,
    cityName: 'Ramos Arizpe',
    placeId: 'ChIJJRdMvxUTiIYRonnI_tJpPdA',
    center: { lat: 25.5287851, lng: -100.9518886 },
    nearbyCities: SALTILLO_CITIES
  },
  [SearchPageCity.Soledad]: {
    city: SearchPageCity.Soledad,
    cityName: 'Soledad de Graciano Sánchez',
    placeId: 'ChIJd4pEP6ihKoQR4VMDvJIKkZA',
    center: { lat: 22.1807042, lng: -100.9417767 },
    nearbyCities: SAN_LUIS_POTOSI_CITIES
  },
  [SearchPageCity.Jiutepec]: {
    city: SearchPageCity.Jiutepec,
    cityName: 'Jiutepec',
    placeId: 'ChIJ2XQJu451zoURv1MGV8Ir198',
    center: { lat: 18.8954874, lng: -99.1732936 },
    nearbyCities: CUERNAVACA_CITIES
  },
  [SearchPageCity.Temixco]: {
    city: SearchPageCity.Temixco,
    cityName: 'Temixco',
    placeId: 'ChIJGzfg9SDZzYURLe2rgyC-ac4',
    center: { lat: 18.8593026, lng: -99.2393666 },
    nearbyCities: CUERNAVACA_CITIES
  },
  [SearchPageCity.Veracruz]: {
    city: SearchPageCity.Veracruz,
    cityName: 'Veracruz',
    placeId: 'ChIJxXjKRUJBw4URmwE26ULWpBg',
    center: { lat: 19.17892, lng: -96.1623967 },
    nearbyCities: STATE_VERACRUZ
  },
  [SearchPageCity.Tampico]: {
    city: SearchPageCity.Tampico,
    cityName: 'Tampico',
    placeId: 'ChIJHy3-_Iv914UR5omwncOaGMM',
    center: { lat: 22.2332, lng: -97.8611 },
    nearbyCities: TAMPICO_CITIES
  },
  [SearchPageCity.CiudadMadero]: {
    city: SearchPageCity.CiudadMadero,
    cityName: 'Ciudad Madero',
    placeId: 'ChIJaauxG1r414URDYpLRsZhiTI',
    center: { lat: 22.2783049, lng: -97.8161995 },
    nearbyCities: TAMPICO_CITIES
  },
  [SearchPageCity.Villahermosa]: {
    city: SearchPageCity.Villahermosa,
    cityName: 'Villahermosa',
    placeId: 'ChIJUwZC15bX7YURlOiKVWJ7YPU',
    center: { lat: 17.9925343, lng: -92.9532307 },
    nearbyCities: STATE_TABASCO
  },
  [SearchPageCity.Morelia]: {
    city: SearchPageCity.Morelia,
    cityName: 'Morelia',
    placeId: 'ChIJ46edsqILLYQRIIacZ3hpAQQ',
    center: { lat: 19.705632, lng: -101.1950164 },
    nearbyCities: STATE_MICHOACAN
  },
  [SearchPageCity.Pachuca]: {
    city: SearchPageCity.Pachuca,
    cityName: 'Pachuca',
    placeId: 'ChIJiwMeb3Gn0YURSbeWFBBcMRM',
    center: { lat: 20.0836725, lng: -98.7644023 },
    nearbyCities: STATE_HIDALGO
  },
  [SearchPageCity.Reynosa]: {
    city: SearchPageCity.Reynosa,
    cityName: 'Reynosa',
    placeId: 'ChIJkbUaC-cJZYYRhEi6XF_IaEU',
    center: { lat: 26.0338654, lng: -98.3055882 },
    nearbyCities: STATE_TAMAULIPAS
  },
  [SearchPageCity.Mazatlan]: {
    city: SearchPageCity.Mazatlan,
    cityName: 'Mazatlán',
    placeId: 'ChIJwTcYaEFTn4YRsnI88arEpGI',
    center: { lat: 23.2473025, lng: -106.4082259 },
    nearbyCities: STATE_SINALOA
  },
  [SearchPageCity.Celaya]: {
    city: SearchPageCity.Celaya,
    cityName: 'Celaya',
    placeId: 'ChIJdcZgHmS6LIQRRyd9mq3Tj5k',
    center: { lat: 20.5283124, lng: -100.8178873 },
    nearbyCities: LEON_CITIES
  },
  [SearchPageCity.Irapuato]: {
    city: SearchPageCity.Irapuato,
    cityName: 'Irapuato',
    placeId: 'ChIJ9SR-ex9-LIQRm2jpzR4I_cc',
    center: { lat: 20.6826253, lng: -101.3578148 },
    nearbyCities: LEON_CITIES
  },
  [SearchPageCity.Silao]: {
    city: SearchPageCity.Silao,
    cityName: 'Silao de la Victoria',
    placeId: 'ChIJSXXrm3icK4QR0r0VsIk7zEs',
    center: { lat: 20.9463, lng: -101.4277269 },
    nearbyCities: LEON_CITIES
  },
  [SearchPageCity.GomezPalacio]: {
    city: SearchPageCity.GomezPalacio,
    cityName: 'Gómez Palacio',
    placeId: 'ChIJr9SXsc7Zj4YRzbjXdRQ7oUI',
    center: { lat: 25.5851699, lng: -103.4813629 },
    nearbyCities: TORREON_CITIES
  },
  [SearchPageCity.Lerdo]: {
    city: SearchPageCity.Lerdo,
    cityName: 'Lerdo',
    placeId: 'ChIJX_RmQYnYj4YRaO-E7cVHm-I',
    center: { lat: 25.5460535, lng: -103.5260571 },
    nearbyCities: TORREON_CITIES
  },
  // Baja California
  [SearchPageCity.Ensenada]: {
    city: SearchPageCity.Ensenada,
    cityName: 'Ensenada',
    placeId: 'ChIJ9blUH_CO2IARUaZ2thuYYPM',
    center: { lat: 31.8557021, lng: -116.6057392 },
    nearbyCities: STATE_BAJA_CALIFORNIA
  },
  // Baja California Sur
  [SearchPageCity.LaPaz]: {
    city: SearchPageCity.LaPaz,
    cityName: 'La Paz',
    placeId: 'ChIJVxDa9d7Sr4YRtqPxwOjSdUg',
    center: { lat: 24.1426408, lng: -110.3127531 },
    nearbyCities: STATE_BAJA_CALIFORNIA_SUR
  },
  [SearchPageCity.CiudadConstitucion]: {
    city: SearchPageCity.CiudadConstitucion,
    cityName: 'Ciudad Constitución',
    placeId: 'ChIJ96VL0NGjs4YRk99dEwt4Hrc',
    center: { lat: 25.0339575, lng: -111.6738127 },
    nearbyCities: STATE_BAJA_CALIFORNIA_SUR
  },
  // Sonora
  [SearchPageCity.CiudadObregon]: {
    city: SearchPageCity.CiudadObregon,
    cityName: 'Ciudad Obregón',
    placeId: 'ChIJuf91y-EVyIYRrhU1zao4HzE',
    center: { lat: 27.4827729, lng: -109.930367 },
    nearbyCities: STATE_SONORA
  },
  [SearchPageCity.Nogales]: {
    city: SearchPageCity.Nogales,
    cityName: 'Nogales',
    placeId: 'ChIJm-hjlLhO0YYR1UnTOdrKLcc',
    center: { lat: 31.3011855, lng: -110.9381047 },
    nearbyCities: STATE_SONORA
  },
  [SearchPageCity.SanLuisRioColorado]: {
    city: SearchPageCity.SanLuisRioColorado,
    cityName: 'San Luis Río Colorado',
    placeId: 'ChIJrZlmMP8F1oARedtdfaKfS4Q',
    center: { lat: 32.4518877, lng: -114.771711 },
    nearbyCities: STATE_SONORA
  },
  [SearchPageCity.Guaymas]: {
    city: SearchPageCity.Guaymas,
    cityName: 'Guaymas',
    placeId: 'ChIJ7x3QuPQVyYYRhPkF6AJU2ZY',
    center: { lat: 27.9110633, lng: -110.9090678 },
    nearbyCities: STATE_SONORA
  },
  [SearchPageCity.Navojoa]: {
    city: SearchPageCity.Navojoa,
    cityName: 'Navojoa',
    placeId: 'ChIJ35MR8hobuIYR1_q3VdPmiwk',
    center: { lat: 27.0727573, lng: -109.4437493 },
    nearbyCities: STATE_SONORA
  },
  [SearchPageCity.AguaPrieta]: {
    city: SearchPageCity.AguaPrieta,
    cityName: 'Agua Prieta',
    placeId: 'ChIJ2XXm1nSn0IYRQvN-1olCrZw',
    center: { lat: 31.3277755, lng: -109.5489617 },
    nearbyCities: STATE_SONORA
  },
  [SearchPageCity.Caborca]: {
    city: SearchPageCity.Caborca,
    cityName: 'Caborca',
    placeId: 'ChIJHYJ2aoYU04YRjNVSgK-O3tY',
    center: { lat: 30.7144574, lng: -112.1616173 },
    nearbyCities: STATE_SONORA
  },
  // Sinaloa
  [SearchPageCity.Culiacan]: {
    city: SearchPageCity.Culiacan,
    cityName: 'Culiacán',
    placeId: 'ChIJj_lHd8fWvIYRzCMt6_Dr2Y0',
    center: { lat: 24.8090649, lng: -107.3940117 },
    nearbyCities: STATE_SINALOA
  },
  [SearchPageCity.LosMochis]: {
    city: SearchPageCity.LosMochis,
    cityName: 'Los Mochis',
    placeId: 'ChIJw-o1tkIvuoYRCIgyUkfDJLY',
    center: { lat: 25.7904657, lng: -108.985882 },
    nearbyCities: STATE_SINALOA
  },
  [SearchPageCity.Guasave]: {
    city: SearchPageCity.Guasave,
    cityName: 'Guasave',
    placeId: 'ChIJczjs56O_u4YR2V5OIG49B0U',
    center: { lat: 25.5666987, lng: -108.4673051 },
    nearbyCities: STATE_SINALOA
  },
  [SearchPageCity.Guamuchil]: {
    city: SearchPageCity.Guamuchil,
    cityName: 'Guamúchil',
    placeId: 'ChIJoYkM4diHu4YRsMjRWcFL4uw',
    center: { lat: 25.4586777, lng: -108.07765 },
    nearbyCities: STATE_SINALOA
  },
  // Chihuahua
  [SearchPageCity.Delicias]: {
    city: SearchPageCity.Delicias,
    cityName: 'Delicias',
    placeId: 'ChIJjRoAy7kV64YRTq8qB1cA2I8',
    center: { lat: 28.1871201, lng: -105.4595306 },
    nearbyCities: STATE_CHIHUAHUA
  },
  [SearchPageCity.Cuauhtemoc]: {
    city: SearchPageCity.Cuauhtemoc,
    cityName: 'Cuauhtémoc',
    placeId: 'ChIJNQ5VCbPLwYYRMXy3FhkHxnI',
    center: { lat: 28.406359, lng: -106.8654066 },
    nearbyCities: STATE_CHIHUAHUA
  },
  [SearchPageCity.HidalgoDelParral]: {
    city: SearchPageCity.HidalgoDelParral,
    cityName: 'Hidalgo del Parral',
    placeId: 'ChIJl0oiDUhRlIYRFogAd1-FElQ',
    center: { lat: 26.9317835, lng: -105.6666166 },
    nearbyCities: STATE_CHIHUAHUA
  },
  [SearchPageCity.NuevoCasasGrandes]: {
    city: SearchPageCity.NuevoCasasGrandes,
    cityName: 'Nuevo Casas Grandes',
    placeId: 'ChIJfSLPL1Su3IYR8MIwOpQQF40',
    center: { lat: 30.4124235, lng: -107.9145008 },
    nearbyCities: STATE_CHIHUAHUA
  },
  // Coahuila
  [SearchPageCity.Monclova]: {
    city: SearchPageCity.Monclova,
    cityName: 'Monclova',
    placeId: 'ChIJiWBfvDMkioYRxC0G1qfEgL0',
    center: { lat: 26.9080378, lng: -101.4215236 },
    nearbyCities: STATE_COAHUILA
  },
  [SearchPageCity.PiedrasNegras]: {
    city: SearchPageCity.PiedrasNegras,
    cityName: 'Piedras Negras',
    placeId: 'ChIJk5GI0pmLX4YRwmG4QrQzSpU',
    center: { lat: 28.6916182, lng: -100.5408622 },
    nearbyCities: STATE_COAHUILA
  },
  [SearchPageCity.Acuna]: {
    city: SearchPageCity.Acuna,
    cityName: 'Acuña',
    placeId: 'ChIJjyF1Vd6U9YYRPSz9824pY3E',
    center: { lat: 29.3212355, lng: -100.9602608 },
    nearbyCities: STATE_COAHUILA
  },
  // Durango
  [SearchPageCity.Durango]: {
    city: SearchPageCity.Durango,
    cityName: 'Durango',
    placeId: 'ChIJSUVzr8GpkIYRsgjjq0HifSk',
    // City proper (Victoria de Durango). The previous value (24.5592665) was
    // the STATE centroid, ~59 km north of town — it centered the map wrong
    // and would have skewed directory hub routing (2026-08-24).
    center: { lat: 24.0277202, lng: -104.6531759 },
    nearbyCities: STATE_DURANGO
  },
  // Zacatecas
  [SearchPageCity.Zacatecas]: {
    city: SearchPageCity.Zacatecas,
    cityName: 'Zacatecas',
    placeId: 'ChIJb_7JfqDRgYYRmG0lXv0tHxw',
    center: { lat: 22.7708555, lng: -102.5832426 },
    nearbyCities: STATE_ZACATECAS
  },
  [SearchPageCity.Fresnillo]: {
    city: SearchPageCity.Fresnillo,
    cityName: 'Fresnillo',
    placeId: 'ChIJIdCtv7EIg4YRK1rB-fgC0KQ',
    center: { lat: 23.1777586, lng: -102.8687414 },
    nearbyCities: STATE_ZACATECAS
  },
  // Nuevo León
  [SearchPageCity.Linares]: {
    city: SearchPageCity.Linares,
    cityName: 'Linares',
    placeId: 'ChIJzXYU7TaTfIYRQI-cZ3hpAQQ',
    center: { lat: 24.8639494, lng: -99.56178469999999 },
    nearbyCities: STATE_NUEVO_LEON
  },
  [SearchPageCity.Montemorelos]: {
    city: SearchPageCity.Montemorelos,
    cityName: 'Montemorelos',
    placeId: 'ChIJgUNW8A_UfIYRtabzXXUCRVU',
    center: { lat: 25.1889176, lng: -99.8363028 },
    nearbyCities: STATE_NUEVO_LEON
  },
  // Tamaulipas
  [SearchPageCity.CiudadVictoria]: {
    city: SearchPageCity.CiudadVictoria,
    cityName: 'Ciudad Victoria',
    placeId: 'ChIJk_G31zJReYYR1gh_fXUAskI',
    center: { lat: 23.7369164, lng: -99.14111539999999 },
    nearbyCities: STATE_TAMAULIPAS
  },
  [SearchPageCity.Matamoros]: {
    city: SearchPageCity.Matamoros,
    cityName: 'Matamoros',
    placeId: 'ChIJhwDRk8eUb4YRXsoTOXB4IXM',
    center: { lat: 25.8690294, lng: -97.50273759999999 },
    nearbyCities: STATE_TAMAULIPAS
  },
  [SearchPageCity.NuevoLaredo]: {
    city: SearchPageCity.NuevoLaredo,
    cityName: 'Nuevo Laredo',
    placeId: 'ChIJj8fEQX8YYYYRHOnTPk9EXXM',
    center: { lat: 27.4779362, lng: -99.549573 },
    nearbyCities: STATE_TAMAULIPAS
  },
  [SearchPageCity.Altamira]: {
    city: SearchPageCity.Altamira,
    cityName: 'Altamira',
    placeId: 'ChIJLfeZYBf914UR3PZ0PvgDlPk',
    center: { lat: 22.4077462, lng: -97.92115550000001 },
    nearbyCities: TAMPICO_CITIES
  },
  [SearchPageCity.ElMante]: {
    city: SearchPageCity.ElMante,
    cityName: 'El Mante',
    placeId: 'ChIJt-kazY7HeIYRym6Zg1exkEk',
    center: { lat: 22.744446, lng: -98.97592519999999 },
    nearbyCities: STATE_TAMAULIPAS
  },
  // San Luis Potosí
  [SearchPageCity.CiudadValles]: {
    city: SearchPageCity.CiudadValles,
    cityName: 'Ciudad Valles',
    placeId: 'ChIJL6n9jKoS1oURiCa8EllgODM',
    center: { lat: 22.0025712, lng: -99.0018424 },
    nearbyCities: STATE_SAN_LUIS_POTOSI
  },
  [SearchPageCity.Matehuala]: {
    city: SearchPageCity.Matehuala,
    cityName: 'Matehuala',
    placeId: 'ChIJ-YJAaYYdfoYRYMScZ3hpAQQ',
    center: { lat: 23.6448029, lng: -100.64279 },
    nearbyCities: STATE_SAN_LUIS_POTOSI
  },
  [SearchPageCity.Rioverde]: {
    city: SearchPageCity.Rioverde,
    cityName: 'Rioverde',
    placeId: 'ChIJW0BNDawM1YUR3xf8z4_L1vY',
    center: { lat: 21.9277923, lng: -99.9856344 },
    nearbyCities: STATE_SAN_LUIS_POTOSI
  },
  // Guanajuato
  [SearchPageCity.Guanajuato]: {
    city: SearchPageCity.Guanajuato,
    cityName: 'Guanajuato',
    placeId: 'ChIJ6_EMi_VzK4QRdB5XZdGw9CU',
    center: { lat: 21.0190145, lng: -101.2573586 },
    nearbyCities: STATE_GUANAJUATO
  },
  [SearchPageCity.Salamanca]: {
    city: SearchPageCity.Salamanca,
    cityName: 'Salamanca',
    placeId: 'ChIJRZJcGgmFLIQRrF6zKUduBec',
    center: { lat: 20.5739314, lng: -101.1957172 },
    nearbyCities: STATE_GUANAJUATO
  },
  // Querétaro
  [SearchPageCity.SanJuanDelRio]: {
    city: SearchPageCity.SanJuanDelRio,
    cityName: 'San Juan del Río',
    placeId: 'ChIJtW6A6WsL04URQ815dp7bvfo',
    center: { lat: 20.3951106, lng: -99.9856344 },
    nearbyCities: STATE_QUERETARO
  },
  // Jalisco
  [SearchPageCity.LagosDeMoreno]: {
    city: SearchPageCity.LagosDeMoreno,
    cityName: 'Lagos de Moreno',
    placeId: 'ChIJO14MpyTSK4QRUaU0txjmpxk',
    center: { lat: 21.3634964, lng: -101.9291015 },
    nearbyCities: STATE_JALISCO
  },
  [SearchPageCity.TepatitlanDeMorelos]: {
    city: SearchPageCity.TepatitlanDeMorelos,
    cityName: 'Tepatitlán de Morelos',
    placeId: 'ChIJewSYtYkoKYQRH2ywmiHYsq4',
    center: { lat: 20.8166951, lng: -102.7624398 },
    nearbyCities: STATE_JALISCO
  },
  [SearchPageCity.CiudadGuzman]: {
    city: SearchPageCity.CiudadGuzman,
    cityName: 'Ciudad Guzmán',
    placeId: 'ChIJM_AHSjKBL4QRhIdfjn_3sJg',
    center: { lat: 19.7050171, lng: -103.4685183 },
    nearbyCities: STATE_JALISCO
  },
  [SearchPageCity.Ocotlan]: {
    city: SearchPageCity.Ocotlan,
    cityName: 'Ocotlán',
    placeId: 'ChIJ72_Ex9rYLoQRfy4rbS4GEgM',
    center: { lat: 20.3417485, lng: -102.7652326 },
    nearbyCities: STATE_JALISCO
  },
  [SearchPageCity.AutlanDeNavarro]: {
    city: SearchPageCity.AutlanDeNavarro,
    cityName: 'Autlán de Navarro',
    placeId: 'ChIJmXZIK8tUJIQR-iLipEfOVHc',
    center: { lat: 19.7671419, lng: -104.3633228 },
    nearbyCities: STATE_JALISCO
  },
  [SearchPageCity.Arandas]: {
    city: SearchPageCity.Arandas,
    cityName: 'Arandas',
    placeId: 'ChIJJYH6JHdMKYQRhhTaCscOcQE',
    center: { lat: 20.705519, lng: -102.346916 },
    nearbyCities: STATE_JALISCO
  },
  // Colima
  [SearchPageCity.Colima]: {
    city: SearchPageCity.Colima,
    cityName: 'Colima',
    placeId: 'ChIJF6JVNFJFJYQR5lX1WvyXtLY',
    center: { lat: 19.2452342, lng: -103.7240868 },
    nearbyCities: STATE_COLIMA
  },
  [SearchPageCity.Manzanillo]: {
    city: SearchPageCity.Manzanillo,
    cityName: 'Manzanillo',
    placeId: 'ChIJGT67FzTUJIQRSw1156rps90',
    center: { lat: 19.0536292, lng: -104.3170724 },
    nearbyCities: STATE_COLIMA
  },
  [SearchPageCity.Tecoman]: {
    city: SearchPageCity.Tecoman,
    cityName: 'Tecomán',
    placeId: 'ChIJdUyt41m2OoQRg8Uk0RAgWvc',
    center: { lat: 18.9173829, lng: -103.8738031 },
    nearbyCities: STATE_COLIMA
  },
  // Nayarit
  [SearchPageCity.Tepic]: {
    city: SearchPageCity.Tepic,
    cityName: 'Tepic',
    placeId: 'ChIJ2889idg1J4QReS0M9EVGeIM',
    center: { lat: 21.5041651, lng: -104.8945887 },
    nearbyCities: STATE_NAYARIT
  },
  [SearchPageCity.Compostela]: {
    city: SearchPageCity.Compostela,
    cityName: 'Compostela',
    placeId: 'ChIJAcJh7dApJ4QRdBUZuwaSvYE',
    center: { lat: 21.2379255, lng: -104.9007613 },
    nearbyCities: STATE_NAYARIT
  },
  [SearchPageCity.SantiagoIxcuintla]: {
    city: SearchPageCity.SantiagoIxcuintla,
    cityName: 'Santiago Ixcuintla',
    placeId: 'ChIJQ5hKgrKoIIQR2iswU9ASIcI',
    center: { lat: 21.8131863, lng: -105.204327 },
    nearbyCities: STATE_NAYARIT
  },
  // placeId + center from `yarn findPlaceIds` (Google Geocoding), never hand-made.
  [SearchPageCity.Bucerias]: {
    city: SearchPageCity.Bucerias,
    cityName: 'Bucerías',
    placeId: 'ChIJM1ycD5VAIYQRj1Qui8Q5dbc',
    center: { lat: 20.7568197, lng: -105.3336087 },
    nearbyCities: STATE_NAYARIT
  },
  // Michoacán
  [SearchPageCity.Uruapan]: {
    city: SearchPageCity.Uruapan,
    cityName: 'Uruapan',
    placeId: 'ChIJUUj-wljiLYQRNJ8jH-KbflM',
    center: { lat: 19.4064492, lng: -102.0430476 },
    nearbyCities: STATE_MICHOACAN
  },
  [SearchPageCity.Zamora]: {
    city: SearchPageCity.Zamora,
    cityName: 'Zamora',
    placeId: 'ChIJDc6O2K2ILoQRKz8GfoKXiKU',
    center: { lat: 19.9901766, lng: -102.2834075 },
    nearbyCities: STATE_MICHOACAN
  },
  [SearchPageCity.LazaroCardenas]: {
    city: SearchPageCity.LazaroCardenas,
    cityName: 'Lázaro Cárdenas',
    placeId: 'ChIJVwPOX25cMYQRIuYIQ2MbDlg',
    center: { lat: 17.9567646, lng: -102.1943485 },
    nearbyCities: STATE_MICHOACAN
  },
  [SearchPageCity.Apatzingan]: {
    city: SearchPageCity.Apatzingan,
    cityName: 'Apatzingán',
    placeId: 'ChIJS1fsRpTkMYQRjmAJG_oJUjw',
    center: { lat: 19.0837305, lng: -102.353674 },
    nearbyCities: STATE_MICHOACAN
  },
  [SearchPageCity.Zitacuaro]: {
    city: SearchPageCity.Zitacuaro,
    cityName: 'Zitácuaro',
    placeId: 'ChIJGTHghjej0oURzhuhxF1rdEw',
    center: { lat: 19.4375829, lng: -100.3558179 },
    nearbyCities: STATE_MICHOACAN
  },
  [SearchPageCity.LaPiedad]: {
    city: SearchPageCity.LaPiedad,
    cityName: 'La Piedad',
    placeId: 'ChIJ-3lXMAEfLIQRHGs6D2y4f34',
    center: { lat: 20.3307372, lng: -102.0443851 },
    nearbyCities: STATE_MICHOACAN
  },
  [SearchPageCity.Sahuayo]: {
    city: SearchPageCity.Sahuayo,
    cityName: 'Sahuayo',
    placeId: 'ChIJvxBryVLjLoQRHPLvBIleytI',
    center: { lat: 20.0587903, lng: -102.7162354 },
    nearbyCities: STATE_MICHOACAN
  },
  [SearchPageCity.CiudadHidalgo]: {
    city: SearchPageCity.CiudadHidalgo,
    cityName: 'Ciudad Hidalgo',
    placeId: 'ChIJCZb153zL0oURKKlh7U9rElk',
    center: { lat: 19.6915425, lng: -100.546488 },
    nearbyCities: STATE_MICHOACAN
  },
  // Estado de México
  [SearchPageCity.Atlacomulco]: {
    city: SearchPageCity.Atlacomulco,
    cityName: 'Atlacomulco',
    placeId: 'ChIJj11hwopY0oUR3GLzdfPEoME',
    center: { lat: 19.7975581, lng: -99.8766825 },
    nearbyCities: STATE_ESTADO_DE_MEXICO
  },
  // Hidalgo
  [SearchPageCity.Tulancingo]: {
    city: SearchPageCity.Tulancingo,
    cityName: 'Tulancingo de Bravo',
    placeId: 'ChIJYU1AVtZV0IURhjERI6tRdDM',
    center: { lat: 20.0904802, lng: -98.3690922 },
    nearbyCities: STATE_HIDALGO
  },
  [SearchPageCity.TulaDeAllende]: {
    city: SearchPageCity.TulaDeAllende,
    cityName: 'Tula de Allende',
    placeId: 'ChIJIzC5Gc_S04URTIs87m6DOe4',
    center: { lat: 20.0557971, lng: -99.34243959999999 },
    nearbyCities: STATE_HIDALGO
  },
  [SearchPageCity.HuejutlaDeReyes]: {
    city: SearchPageCity.HuejutlaDeReyes,
    cityName: 'Huejutla de Reyes',
    placeId: 'ChIJu61FefUm14URhYkQ1rPlOOE',
    center: { lat: 21.1392412, lng: -98.4189061 },
    nearbyCities: STATE_HIDALGO
  },
  // Puebla
  [SearchPageCity.Tehuacan]: {
    city: SearchPageCity.Tehuacan,
    cityName: 'Tehuacán',
    placeId: 'ChIJJ0lHm7uCxYUR6xHSB7xSFBg',
    center: { lat: 18.4665063, lng: -97.40038009999999 },
    nearbyCities: STATE_PUEBLA
  },
  [SearchPageCity.SanMartinTexmelucan]: {
    city: SearchPageCity.SanMartinTexmelucan,
    cityName: 'San Martín Texmelucan',
    placeId: 'ChIJfaRT52nUz4URGaB1GKf3VsY',
    center: { lat: 19.284383, lng: -98.43487300000001 },
    nearbyCities: STATE_PUEBLA
  },
  [SearchPageCity.Atlixco]: {
    city: SearchPageCity.Atlixco,
    cityName: 'Atlixco',
    placeId: 'ChIJNfkV4O6zz4UR5wD3-aMSS1w',
    center: { lat: 18.9131227, lng: -98.4293891 },
    nearbyCities: STATE_PUEBLA
  },
  [SearchPageCity.Teziutlan]: {
    city: SearchPageCity.Teziutlan,
    cityName: 'Teziutlán',
    placeId: 'ChIJtz_YNIrp2oURiR5LvLc6YoY',
    center: { lat: 19.8160106, lng: -97.35705759999999 },
    nearbyCities: STATE_PUEBLA
  },
  // Tlaxcala
  [SearchPageCity.Tlaxcala]: {
    city: SearchPageCity.Tlaxcala,
    cityName: 'Tlaxcala',
    placeId: 'ChIJE2EAHD_Zz4UR4L181Friujg',
    center: { lat: 19.318154, lng: -98.2374954 },
    nearbyCities: STATE_TLAXCALA
  },
  [SearchPageCity.Apizaco]: {
    city: SearchPageCity.Apizaco,
    cityName: 'Apizaco',
    placeId: 'ChIJ3b2gsxgg0IURPstLY81Elqk',
    center: { lat: 19.415881, lng: -98.1393179 },
    nearbyCities: STATE_TLAXCALA
  },
  [SearchPageCity.Huamantla]: {
    city: SearchPageCity.Huamantla,
    cityName: 'Huamantla',
    placeId: 'ChIJHTnGPYr7z4URN3oZA0D5Skw',
    center: { lat: 19.3184941, lng: -97.92338260000001 },
    nearbyCities: STATE_TLAXCALA
  },
  // Morelos
  [SearchPageCity.Cuautla]: {
    city: SearchPageCity.Cuautla,
    cityName: 'Cuautla',
    placeId: 'ChIJdzDIc9duzoURxMSlFsczXn0',
    center: { lat: 18.8126047, lng: -98.9548261 },
    nearbyCities: STATE_MORELOS
  },
  // Guerrero
  [SearchPageCity.Acapulco]: {
    city: SearchPageCity.Acapulco,
    cityName: 'Acapulco',
    placeId: 'ChIJyVDOroVXyoUR46SQivfYAZg',
    center: { lat: 16.8639515, lng: -99.8822807 },
    nearbyCities: STATE_GUERRERO
  },
  [SearchPageCity.Chilpancingo]: {
    city: SearchPageCity.Chilpancingo,
    cityName: 'Chilpancingo',
    placeId: 'ChIJpYZxmOiTy4URlu9lWBLvhWw',
    center: { lat: 17.5508259, lng: -99.5014623 },
    nearbyCities: STATE_GUERRERO
  },
  [SearchPageCity.Iguala]: {
    city: SearchPageCity.Iguala,
    cityName: 'Iguala',
    placeId: 'ChIJpwZianQ4zIURK8RkjFxf3lc',
    center: { lat: 18.3448477, lng: -99.5397344 },
    nearbyCities: STATE_GUERRERO
  },
  // Oaxaca
  [SearchPageCity.Oaxaca]: {
    city: SearchPageCity.Oaxaca,
    cityName: 'Oaxaca de Juárez',
    placeId: 'ChIJsdkm30kix4URO_zfV3aniKw',
    center: { lat: 17.0731842, lng: -96.7265889 },
    nearbyCities: STATE_OAXACA
  },
  [SearchPageCity.Tuxtepec]: {
    city: SearchPageCity.Tuxtepec,
    cityName: 'San Juan Bautista Tuxtepec',
    placeId: 'ChIJVXOK45fmw4URFf04OVi-zuU',
    center: { lat: 18.0809028, lng: -96.1420947 },
    nearbyCities: STATE_OAXACA
  },
  [SearchPageCity.Juchitan]: {
    city: SearchPageCity.Juchitan,
    cityName: 'Juchitán de Zaragoza',
    placeId: 'ChIJ2SgAMuNUlYURNc5j9J3Nq7Q',
    center: { lat: 16.4406909, lng: -95.0221197 },
    nearbyCities: STATE_OAXACA
  },
  [SearchPageCity.SalinaCruz]: {
    city: SearchPageCity.SalinaCruz,
    cityName: 'Salina Cruz',
    placeId: 'ChIJI_XUORuVv4UR9A1K9PPe_Fg',
    center: { lat: 16.1963864, lng: -95.20178 },
    nearbyCities: STATE_OAXACA
  },
  [SearchPageCity.HuajuapanDeLeon]: {
    city: SearchPageCity.HuajuapanDeLeon,
    cityName: 'Huajuapan de León',
    placeId: 'ChIJo4CneQYCxoURmuusWJujJrg',
    center: { lat: 17.8112504, lng: -97.78034819999999 },
    nearbyCities: STATE_OAXACA
  },
  // Veracruz
  [SearchPageCity.Xalapa]: {
    city: SearchPageCity.Xalapa,
    cityName: 'Xalapa',
    placeId: 'ChIJ2SXyoRwy24URfKRAQ7w3SFg',
    center: { lat: 19.5437751, lng: -96.91018059999999 },
    nearbyCities: STATE_VERACRUZ
  },
  [SearchPageCity.Coatzacoalcos]: {
    city: SearchPageCity.Coatzacoalcos,
    cityName: 'Coatzacoalcos',
    placeId: 'ChIJy7PhrlCB6YURpKXNqVlhN-M',
    center: { lat: 18.1497687, lng: -94.41858099999999 },
    nearbyCities: STATE_VERACRUZ
  },
  [SearchPageCity.Cordoba]: {
    city: SearchPageCity.Cordoba,
    cityName: 'Córdoba',
    placeId: 'ChIJs1cSfwqNxIURMn6DEmxACLs',
    center: { lat: 18.8838909, lng: -96.9237751 },
    nearbyCities: STATE_VERACRUZ
  },
  [SearchPageCity.Orizaba]: {
    city: SearchPageCity.Orizaba,
    cityName: 'Orizaba',
    placeId: 'ChIJBZ9DwKQCxYURgFcna4OqP9g',
    center: { lat: 18.8504744, lng: -97.1036396 },
    nearbyCities: STATE_VERACRUZ
  },
  [SearchPageCity.PozaRica]: {
    city: SearchPageCity.PozaRica,
    cityName: 'Poza Rica',
    placeId: 'ChIJCQltgM0U2oURKR0EaRdlxns',
    center: { lat: 20.5316605, lng: -97.4605184 },
    nearbyCities: STATE_VERACRUZ
  },
  [SearchPageCity.Minatitlan]: {
    city: SearchPageCity.Minatitlan,
    cityName: 'Minatitlán',
    placeId: 'ChIJHXtb6fCH6YURLyHSzOyIhJ8',
    center: { lat: 17.9859818, lng: -94.54519379999999 },
    nearbyCities: STATE_VERACRUZ
  },
  [SearchPageCity.Tuxpan]: {
    city: SearchPageCity.Tuxpan,
    cityName: 'Tuxpan',
    placeId: 'ChIJIbSQdbTy2YURaF1_pNCBjFQ',
    center: { lat: 20.9561149, lng: -97.40633509999999 },
    nearbyCities: STATE_VERACRUZ
  },
  [SearchPageCity.SanAndresTuxtla]: {
    city: SearchPageCity.SanAndresTuxtla,
    cityName: 'San Andrés Tuxtla',
    placeId: 'ChIJlds5_wETwoURl7I0xt8_WWg',
    center: { lat: 18.4441222, lng: -95.2130284 },
    nearbyCities: STATE_VERACRUZ
  },
  [SearchPageCity.MartinezDeLaTorre]: {
    city: SearchPageCity.MartinezDeLaTorre,
    cityName: 'Martínez de la Torre',
    placeId: 'ChIJsWrN7XH92oURKOXz6hSOThI',
    center: { lat: 20.0624883, lng: -97.05564299999999 },
    nearbyCities: STATE_VERACRUZ
  },
  // Tabasco
  [SearchPageCity.Cardenas]: {
    city: SearchPageCity.Cardenas,
    cityName: 'Cárdenas',
    placeId: 'ChIJR7ud4ZEg7IURj-YUWSAnTkk',
    center: { lat: 18.0012067, lng: -93.37323390000002 },
    nearbyCities: STATE_TABASCO
  },
  [SearchPageCity.Comalcalco]: {
    city: SearchPageCity.Comalcalco,
    cityName: 'Comalcalco',
    placeId: 'ChIJrSTYmcCI7oUR-QotOQFjEkA',
    center: { lat: 18.261156, lng: -93.2216864 },
    nearbyCities: STATE_TABASCO
  },
  // Chiapas
  [SearchPageCity.TuxtlaGutierrez]: {
    city: SearchPageCity.TuxtlaGutierrez,
    cityName: 'Tuxtla Gutiérrez',
    placeId: 'ChIJj27QEVbY7IURaiFiRnu9eN0',
    center: { lat: 16.7516009, lng: -93.1029939 },
    nearbyCities: STATE_CHIAPAS
  },
  [SearchPageCity.Tapachula]: {
    city: SearchPageCity.Tapachula,
    cityName: 'Tapachula',
    placeId: 'ChIJscFxXjkPjoURiyK4ALi5KF0',
    center: { lat: 14.9055567, lng: -92.26341359999999 },
    nearbyCities: STATE_CHIAPAS
  },
  [SearchPageCity.SanCristobalDeLasCasas]: {
    city: SearchPageCity.SanCristobalDeLasCasas,
    cityName: 'San Cristóbal de las Casas',
    placeId: 'ChIJz5uoHjtF7YUR3uJXSaNc1Ug',
    center: { lat: 16.7366198, lng: -92.6388466 },
    nearbyCities: STATE_CHIAPAS
  },
  [SearchPageCity.Comitan]: {
    city: SearchPageCity.Comitan,
    cityName: 'Comitán',
    placeId: 'ChIJzdzd9rI4jYUREIShKdFqrjw',
    center: { lat: 16.2347403, lng: -92.1339541 },
    nearbyCities: STATE_CHIAPAS
  },
  // Campeche
  [SearchPageCity.Campeche]: {
    city: SearchPageCity.Campeche,
    cityName: 'Campeche',
    placeId: 'ChIJbRNrF5Yz-IURRGLylUyOPek',
    center: { lat: 19.8301251, lng: -90.5349087 },
    nearbyCities: STATE_CAMPECHE
  },
  [SearchPageCity.CiudadDelCarmen]: {
    city: SearchPageCity.CiudadDelCarmen,
    cityName: 'Ciudad del Carmen',
    placeId: 'ChIJxe8nVwkH8YURxZF2aTCRX50',
    center: { lat: 18.6504879, lng: -91.80745859999999 },
    nearbyCities: STATE_CAMPECHE
  },
  // Yucatán
  [SearchPageCity.Valladolid]: {
    city: SearchPageCity.Valladolid,
    cityName: 'Valladolid',
    placeId: 'ChIJ9_O-e9oKUY8RMao55Y8feu0',
    center: { lat: 20.68964, lng: -88.20224879999999 },
    nearbyCities: STATE_YUCATAN
  },
  [SearchPageCity.Tizimin]: {
    city: SearchPageCity.Tizimin,
    cityName: 'Tizimín',
    placeId: 'ChIJw6-NMRQrUo8R1xBA9XwOuKI',
    center: { lat: 21.1454686, lng: -88.1496087 },
    nearbyCities: STATE_YUCATAN
  },
  // Quintana Roo
  [SearchPageCity.Chetumal]: {
    city: SearchPageCity.Chetumal,
    cityName: 'Chetumal',
    placeId: 'ChIJD_nXLFWjW48R7VO18NfsSSE',
    center: { lat: 18.5001889, lng: -88.296146 },
    nearbyCities: STATE_QUINTANA_ROO
  },
  [SearchPageCity.Cozumel]: {
    city: SearchPageCity.Cozumel,
    cityName: 'Cozumel',
    placeId: 'ChIJJ5PRsO_4To8R_IsBXK68LjQ',
    center: { lat: 20.4229839, lng: -86.9223432 },
    nearbyCities: STATE_QUINTANA_ROO
  }
}

export const HOOD_MAP: {
  [key in SearchHood]: { hoodName: string; placeId: string }
} = {
  [MexicoCityHoods.Anzures]: {
    hoodName: 'Anzures',
    placeId: 'ChIJ2cEpRk3_0YURe0nsHZ-75g0'
  },
  [MexicoCityHoods.Azcapotzalco]: {
    hoodName: 'Azcapotzalco',
    placeId: 'ChIJzRm0I9P50YURcbV6UCCW_yQ'
  },
  [MexicoCityHoods.Centro]: {
    hoodName: 'Centro',
    placeId: 'ChIJ8bh9yyz50YURlL2cFjXnr98'
  },
  [MexicoCityHoods.Coyoacan]: {
    hoodName: 'Coyoacan',
    placeId: 'ChIJwy80ES4AzoURgODUMdhnJpo'
  },
  [MexicoCityHoods.Condesa]: {
    hoodName: 'Condesa',
    placeId: 'ChIJ4zXmiUT_0YUR57jJi24HHO0'
  },
  [MexicoCityHoods.ColoniaJuarez]: {
    hoodName: 'Colonia Juarez',
    placeId: 'ChIJBwSKbzT_0YURL8cF7c7Nctk'
  },
  [MexicoCityHoods.DeValle]: {
    hoodName: 'Del Valle',
    placeId: 'ChIJ-f36vp__0YUR80l67xodJ1I'
  },
  [MexicoCityHoods.Doctores]: {
    hoodName: 'Doctores',
    placeId: 'ChIJP1kS1yb_0YURtc9uxpN8P7o'
  },
  [MexicoCityHoods.Escandon]: {
    hoodName: 'Escandon',
    placeId: 'ChIJfyqLqmj_0YURY_XtfNcVz1U'
  },
  [MexicoCityHoods.Guerrero]: {
    hoodName: 'Guerrero',
    placeId: 'ChIJsSu1Myb50YURnMmlJi0w05o'
  },
  [MexicoCityHoods.Iztapalapa]: {
    hoodName: 'Iztapalapa',
    placeId: 'ChIJiQcnB4ICzoURqfhU7FCS5cE'
  },
  [MexicoCityHoods.JardinesDelPedregal]: {
    hoodName: 'Jardines del Pedregal',
    placeId: 'ChIJxbqHQez_zYURC023DLxszcs'
  },
  [MexicoCityHoods.LasLomas]: {
    hoodName: 'Las Lomas',
    placeId: 'ChIJjVCsD44B0oUR_zUzt8kjJqw'
  },
  [MexicoCityHoods.LomasDeChapultepec]: {
    hoodName: 'Lomas de Chapultepec',
    placeId: 'ChIJjVCsD44B0oUR_zUzt8kjJqw'
  },
  [MexicoCityHoods.Napoles]: {
    hoodName: 'Napoles',
    placeId: 'ChIJVVDq_XD_0YURqBXij6GFdPQ'
  },
  [MexicoCityHoods.Naucalpan]: {
    hoodName: 'Naucalpan',
    placeId: 'ChIJ7y7PWEkB0oURn-ssxjwYNsA'
  },
  [MexicoCityHoods.Narvarte]: {
    hoodName: 'Narvarte',
    placeId: 'ChIJ8Yd8Wwb_0YURSBv8PSVer4E'
  },
  [MexicoCityHoods.Obrera]: {
    hoodName: 'Obrera',
    placeId: 'ChIJt6H2Ptz-0YURradu-E-kIek'
  },
  [MexicoCityHoods.Polanco]: {
    hoodName: 'Polanco',
    placeId: 'ChIJ-UNIbQMC0oURdqnTwDJ30-Q'
  },
  [MexicoCityHoods.Roma]: {
    hoodName: 'Roma',
    placeId: 'ChIJzeSi8jn_0YURMiXDZ-gzmi0'
  },
  [MexicoCityHoods.RomaNorte]: {
    hoodName: 'Roma Norte',
    placeId: 'ChIJzeSi8jn_0YURMiXDZ-gzmi0'
  },
  [MexicoCityHoods.RomaSur]: {
    hoodName: 'Roma Sur',
    placeId: 'ChIJzeSi8jn_0YURMiXDZ-gzmi0'
  },
  [MexicoCityHoods.SanAngel]: {
    hoodName: 'San Angel',
    placeId: 'ChIJ8_W4AQMA0oURuyq-JGd9Cms'
  },
  [MexicoCityHoods.SantaFe]: {
    hoodName: 'Santa Fe',
    placeId: 'ChIJY1InSKwB0oURb6dkqE4h63Q'
  },
  [MexicoCityHoods.SanRafael]: {
    hoodName: 'San Rafael',
    placeId: 'ChIJj9Tx8cv40YURKcx2uQSxoYs'
  },
  [MexicoCityHoods.Tepito]: {
    hoodName: 'Tepito',
    placeId: 'ChIJA_8cTjr50YURKmq5IIv_l9E'
  },
  [MexicoCityHoods.Tlalnepantla]: {
    hoodName: 'Tlalnepantla',
    placeId: 'ChIJYxCNbHb40YUR9s1Fdr8kGJk'
  },
  [MexicoCityHoods.Tlatelolco]: {
    hoodName: 'Tlatelolco',
    placeId: 'ChIJeZh2bSH50YURRFVyBPkQKgQ'
  },
  [MexicoCityHoods.ValleDeChalco]: {
    hoodName: 'Valle de Chalco',
    placeId: 'ChIJ7z-C0zUczoURDvbvTV8zbJE'
  },
  [MexicoCityHoods.Ecatepec]: {
    hoodName: 'Ecatepec de Morelos',
    placeId: 'ChIJoXf3chzy0YURgGayQgpU1Ew'
  },
  [MexicoCityHoods.CuautitlanIzcalli]: {
    hoodName: 'Cuautitlán Izcalli',
    placeId: 'ChIJZ0Krr9Qd0oURiE3bnk3X6Aw'
  },
  [MexicoCityHoods.Tultitlan]: {
    hoodName: 'Tultitlán',
    placeId: 'ChIJiZsKVsD10YURSP79Wtrb6-Y'
  },
  [MexicoCityHoods.AtizapanDeZaragoza]: {
    hoodName: 'Atizapán de Zaragoza',
    placeId: 'ChIJ__vTAocc0oUR4MjngvxrWsU'
  },
  [MexicoCityHoods.Texcoco]: {
    hoodName: 'Texcoco',
    placeId: 'ChIJdTzvVUPm0YURxIjzoqaj6E8'
  },
  [MexicoCityHoods.Chalco]: {
    hoodName: 'Chalco',
    placeId: 'ChIJDcgeOsgezoUROPRs4F3AIhY'
  },
  [MexicoCityHoods.Xochimilco]: {
    hoodName: 'Xochimilco',
    placeId: 'ChIJo97obYwDzoURhFO92h_3eZs'
  },
  [MexicoCityHoods.Tlalpan]: {
    hoodName: 'Tlalpan',
    placeId: 'ChIJ91ztH5YAzoURH2Ut_9lGzYM'
  },
  [MexicoCityHoods.Mixcoac]: {
    hoodName: 'Mixcoac',
    placeId: 'ChIJC9t1iYf_0YUR1FyX1IqgAac'
  },
  [MexicoCityHoods.Lindavista]: {
    hoodName: 'Lindavista',
    placeId: 'ChIJ-QpWbbv50YURrTd5fgycqdw'
  }
}

export const SEARCH_LINKS_MAP: {
  [key in PillarPage]: { [key in SearchPageCity]: string }
} = Object.values(PillarPage).reduce(
  (memo, page) => {
    return {
      ...memo,
      [page]: Object.values(SearchPageCity).reduce((memo, city) => {
        return {
          ...memo,
          [city]: `/${page}${city ? `/${city}` : ''}`
        }
      }, {})
    }
  },
  {} as { [key in PillarPage]: { [key in SearchPageCity]: string } }
)
